/** Meeting capture: recording center + assistant panel, live transcript, finalize, schedule tasks. */
import { httpsCallable } from "firebase/functions";
import { $, $$, byId, esc, on, toast } from "../lib/dom";
import { fmtClock, fmtMMSS, fmtLongDate, relativeDue, fmtShortDate, fmtRange } from "../lib/time";
import { functions } from "../lib/firebase";
import type { Meeting, TranscriptSegment } from "../data/types";
import { addManualNote, createMeeting, createTask, updateMeeting, updateTask, watchMeeting, watchTranscript } from "../data/store";
import { sameDay } from "../lib/time";
import { canCaptureCall, ChunkedRecorder, grabMicrophone, type CaptureSource, type LevelState } from "../capture/recorder";
import { state, patch } from "../state";
import { dictateInto, goTo, onPageChange } from "./shell";
import { openTaskDetail } from "./tasks";
import { showWeekOf } from "./calendar";
import { createOrb, type Orb } from "./orb";
import { pickRecording } from "./upload";

let recorder: ChunkedRecorder | null = null;
let unwatchMeeting: (() => void) | null = null;
let unwatchTranscript: (() => void) | null = null;
let viewing: Meeting | null = null;
let segments: TranscriptSegment[] = [];
let paused = false;
let heroOrb: Orb | null = null;
let cardOrb: Orb | null = null;
let peekOrb: Orb | null = null;
let buttonOrb: Orb | null = null;
let barOrb: Orb | null = null;

const shareMeetingFn = httpsCallable<{ meetingId: string; origin: string; includeTranscript?: boolean; emails?: string[]; note?: string; attachPdf?: boolean }, { id: string; url: string; sent: number }>(functions, "shareMeeting");
const revokeShareFn = httpsCallable<{ meetingId: string }, { ok: boolean }>(functions, "revokeMeetingShare");
const exportMeetingFn = httpsCallable<{ meetingId: string; format: "pdf" | "doc"; includeTranscript?: boolean }, { filename: string; mime: string; base64: string }>(functions, "exportMeeting");
const finalizeMeeting = httpsCallable<{ meetingId: string }, { ok: boolean }>(functions, "finalizeMeeting");
const askMeeting = httpsCallable<{ meetingId: string; question: string }, { answer: string; citations: { at: string; text: string }[] }>(functions, "askMeeting");
const deleteMeetingFn = httpsCallable<{ meetingId: string }, { ok: boolean }>(functions, "deleteMeeting");
const scheduleTasks = httpsCallable<{ taskIds: string[] }, { results: { taskId: string; eventId?: string; start?: string; end?: string; reason?: string }[] }>(functions, "scheduleTasks");

const suggestedCount = (meetingId: string) => state.tasks.filter((t) => t.meetingId === meetingId && t.status === "suggested").length;
/** The notes need room the moment they matter: when a recording finishes, and while you edit a line. */
function setExpanded(on: boolean) { byId("meetingAssistant")?.classList.toggle("expanded", on); }
let lastStatus: Record<string, string> = {};

/** "just now", "12 min ago", "3 h ago", "yesterday", "Sep 8". */
function relTime(d: Date) {
  const min = Math.round((Date.now() - d.getTime()) / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  if (h < 48) return "yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Idle: Record + no level meter. Live: Pause/Stop + level meter. */
function setLiveControls(live: boolean) {
  document.dispatchEvent(new CustomEvent("timo:recording-state", { detail: live }));
  const start = byId("startBtn"), startCall = byId("startCallBtn"), pause = byId("pauseBtn"), stop = byId("stopBtn"), health = byId("audioHealth"), mute = byId("muteBtn");
  const orbHost = document.querySelector<HTMLElement>(".record-icon");
  if (orbHost) { orbHost.classList.toggle("card-orb", !live); orbHost.title = live ? "" : "Start recording (R)"; }
  if (mute) { mute.style.display = live ? "" : "none"; (mute as HTMLButtonElement).disabled = !live; }
  if (start) start.style.display = live ? "none" : "";
  if (startCall) startCall.style.display = live ? "none" : "";
  const srcEl = byId("captureSource"); if (srcEl && !live) srcEl.hidden = true;
  if (pause) { pause.style.display = live ? "" : "none"; (pause as HTMLButtonElement).disabled = !live; }
  if (stop) { stop.style.display = live ? "" : "none"; (stop as HTMLButtonElement).disabled = !live; }
  if (health) health.style.display = live ? "" : "none";
  const rec = $(".recording");
  if (rec && !live) rec.textContent = viewing && viewing.status === "complete" ? "Last meeting captured" : viewing?.status === "processing" ? "Finalizing…" : "Not recording";
}

const orbs = () => [heroOrb, buttonOrb, barOrb, cardOrb, peekOrb];
const setOrbs = (st: "idle" | "recording" | "paused" | "processing") => orbs().forEach((o) => o?.setState(st));

/** One line under the big orb that says what a tap does right now. Plain words, no jargon. */
function paintOrbHint() {
  const el = byId("orbHint");
  if (!el) return;
  el.textContent = !recorder ? "Tap to start recording"
    : recorder.microphoneLost ? "Timo continues when the call ends"
    : paused ? "Tap to continue"
    : recorder.micMuted ? "Tap twice to unmute"
    : "Tap to pause. Tap twice to mute";
}

/** A call on this phone took the microphone. Say so, and take it back when it is free. */
function paintMicLost(lost: boolean) {
  const rec = $(".recording");
  if (lost) {
    if (rec) rec.innerHTML = '<i class="pulse"></i>Another app has the microphone';
    byId("audioHealthTitle")!.textContent = "The call app has the microphone";
    byId("audioHealthHelp")!.textContent = "Phones give the microphone to the call alone. Timo keeps the meeting open and starts hearing again when the call ends.";
    byId("audioHealth")!.className = "audio-health warning";
    setOrbs("paused");
    toast("A call took the microphone. Timo picks up again when it ends");
  } else {
    if (rec && !paused) rec.innerHTML = '<i class="pulse"></i>Recording in progress';
    setOrbs(paused ? "paused" : "recording");
    toast("Microphone is back. Recording again");
  }
  paintOrbHint();
}

const showCenter = (show: boolean) => {
  byId("recordingCenter")!.style.display = show ? "block" : "none";
  byId("page-today")!.classList.toggle("recording-mode", show);
  const ret = $(".live-return") as HTMLElement | null;
  if (ret) ret.style.display = !show && recorder ? "flex" : "none";
};

/**
 * The calendar meeting a new recording most plausibly belongs to, or none.
 *
 * Borrowing its title is a kindness when it is right and an annoyance when it is not - every
 * recording one afternoon came out named "GPH". The old match took the first meeting anywhere
 * within fifteen minutes either side: an all-day block, a meeting that had already finished, one
 * not due to start for a quarter of an hour, whichever the list happened to hold first. Now it has
 * to be a real meeting, about to start or under way and not nearly over, and the closest one wins.
 */
function meetingOnNow(now: number) {
  const MIN = 60_000;
  const candidates = state.events.filter((e) => {
    if (e.kind !== "meeting") return false;
    const start = e.start.getTime(), end = e.end.getTime(), length = end - start;
    if (length <= 0 || length > 3 * 60 * MIN) return false;      // all-day blocks and long holds
    return start <= now + 10 * MIN && end >= now + 5 * MIN;      // starting soon, or still going
  });
  candidates.sort((a, b) => Math.abs(a.start.getTime() - now) - Math.abs(b.start.getTime() - now));
  return candidates[0];
}

/**
 * True from the moment Start is pressed until the recorder exists. The old guard only checked for a
 * recorder, which is not created until a meeting has been written - so a second click in that gap,
 * the natural thing to do when nothing seems to happen, started a second meeting and a second
 * recorder on the same microphone, one of them running unseen.
 */
let starting = false;

export async function startMeeting(title?: string, source: CaptureSource = "mic", preAcquired?: MediaStream | null) {
  if (recorder || starting) { showCenter(true); return goTo("today"); }
  starting = true;
  try { return await startMeetingNow(title, source, preAcquired); } finally { starting = false; }
}

async function startMeetingNow(title?: string, source: CaptureSource = "mic", preAcquired?: MediaStream | null) {
  const now = Date.now();
  const live = meetingOnNow(now);
  const name = title ?? live?.title ?? `Meeting · ${new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  let id = "";
  try {
    id = await createMeeting(name);
    recorder = new ChunkedRecorder(id, {
      onTick: (s) => { byId("timer")!.textContent = fmtClock(s); byId("centerTimer")!.firstChild!.nodeValue = fmtClock(s); },
      onLevel: (rms, level, bars) => { if (level !== "silent" && savedTitle) quietWarning(null); setAudioHealth(level, bars); heroOrb?.setLevel(rms); barOrb?.setLevel(rms); cardOrb?.setLevel(rms); peekOrb?.setLevel(rms); },
      onQueue: (pending, uploaded) => { const el = byId("chunkHealth"); if (el) el.textContent = pending ? `${pending} segment${pending > 1 ? "s" : ""} waiting to upload` : `${uploaded} segments safely uploaded`; },
      onError: (m) => toast(m),
      onCallEnded: () => { toast("Call audio stopped. Still recording from your microphone"); void updateMeeting(id, { captureSource: "mic" }); paintSource(); },
      onMicLost: () => paintMicLost(true),
      onMicBack: () => paintMicLost(false),
      onQuiet: (_quiet, left) => quietWarning(left),
      onQuietStop: () => { quietWarning(null); toast("Stopped - nothing was said for five minutes"); void stopMeeting(); },
    });
    await recorder.start(source, preAcquired);
    if (recorder.source === "call") void updateMeeting(id, { captureSource: "call" });
    paintSource();
    paused = false;
    heroOrb?.setState("recording"); buttonOrb?.setState("recording"); barOrb?.setState("recording"); cardOrb?.setState("recording"); peekOrb?.setState("recording");
    patch({ activeMeetingId: id });
    bindMeeting(id);
    $(".recording")!.innerHTML = '<i class="pulse"></i>Recording in progress';
    byId("stopBtn")!.innerHTML = '<svg class="ui-icon" viewBox="0 0 24 24"><rect x="7" y="7" width="10" height="10" rx="1"></rect></svg><span>Stop</span>';
    (byId("stopBtn") as HTMLButtonElement).disabled = false;
    (byId("pauseBtn") as HTMLButtonElement).disabled = false;
    (byId("centerStopBtn") as HTMLButtonElement).disabled = false;
    byId("centerStopBtn")!.querySelector("span")!.textContent = "Stop meeting";
    byId("meetingProcessing")!.classList.remove("show");
    if (processOrb) { processOrb.destroy(); processOrb = null; document.querySelector(".processing-orbit")?.classList.remove("has-orb"); }
    stopProcessingTicker();
    document.dispatchEvent(new CustomEvent("timo:assistant", { detail: true }));
    setLiveControls(true);
    paintOrbHint();
    goTo("today");
    showCenter(true);
    toast(recorder.synthetic ? "Recording with the local stand-in microphone (emulator)" : recorder.source === "call" ? "Recording you and the call. Timo transcribes both sides" : "Recording. Timo transcribes as you go");
  } catch (err) {
    recorder = null;
    preAcquired?.getTracks().forEach((t) => t.stop());
    // Nothing was captured, so don't leave an empty "recording" meeting behind.
    if (id) void deleteMeetingFn({ meetingId: id }).catch(() => {});
    toast((err as Error).message.includes("Permission") ? "Microphone access is needed to record" : (err as Error).message);
  }
}

/** Shows which sides of the conversation are being captured, under the live status line. */
function paintSource() {
  const el = byId("captureSource");
  if (!el) return;
  el.hidden = !recorder;
  el.textContent = recorder?.source === "call" ? "Mic + call audio" : "Microphone";
}

let lastRenderKey = "";
/** Cheap re-render hook from the global state loop; only redraws when the data the panel shows has changed. */
export function refreshAssistant() {
  if (!viewing) return;
  const key = state.tasks.filter((t) => t.meetingId === viewing!.id).map((t) => `${t.id}:${t.status}`).join(",");
  if (key === lastRenderKey) return;
  lastRenderKey = key;
  renderAssistant();
  setLiveControls(!!recorder);
}

function bindMeeting(id: string) {
  unwatchMeeting?.(); unwatchTranscript?.();
  unwatchMeeting = watchMeeting(id, (m) => {
    if (m && lastStatus[m.id] === "processing" && m.status === "complete") { setExpanded(true); toast("Meeting notes are ready. Check them over"); }
    if (m) lastStatus = { ...lastStatus, [m.id]: m.status };
    viewing = m; renderAssistant(); setLiveControls(!!recorder && recorder.meetingId === id); });
  unwatchTranscript = watchTranscript(id, (s) => { segments = s; renderTranscript(); });
}

export function openMeeting(id: string) {
  bindMeeting(id);
  document.dispatchEvent(new CustomEvent("timo:assistant", { detail: true }));
  if (state.activeMeetingId === id && recorder) { goTo("today"); showCenter(true); }
}

export const isRecording = () => !!recorder;

function toggleMute() {
  if (!recorder) return;
  recorder.setMicMuted(!recorder.micMuted);
  const muted = recorder.micMuted;
  $$("#muteBtn span, #centerMuteBtn span").forEach((el) => (el.textContent = muted ? "Unmute" : "Mute"));
  $$("#muteBtn, #centerMuteBtn").forEach((el) => el.classList.toggle("muted", muted));
  const rec = $(".recording");
  if (rec) rec.innerHTML = muted ? '<i class="pulse"></i>Mic muted · still recording' : '<i class="pulse"></i>Recording in progress';
  heroOrb?.setState(muted ? "idle" : "recording"); buttonOrb?.setState(muted ? "idle" : "recording"); barOrb?.setState(muted ? "idle" : "recording"); cardOrb?.setState(muted ? "idle" : "recording"); peekOrb?.setState(muted ? "idle" : "recording");
  toast(muted ? "Microphone muted. The meeting keeps running" : "Microphone on");
  paintOrbHint();
}

async function togglePause() {
  if (!recorder) return;
  paused = !paused;
  paused ? recorder.pause() : recorder.resume();
  heroOrb?.setState(paused ? "paused" : "recording"); buttonOrb?.setState(paused ? "paused" : "recording"); barOrb?.setState(paused ? "paused" : "recording"); cardOrb?.setState(paused ? "paused" : "recording"); peekOrb?.setState(paused ? "paused" : "recording");
  $$("#pauseBtn span, #centerPauseBtn span").forEach((s) => (s.textContent = paused ? "Resume" : "Pause"));
  $(".recording")!.innerHTML = paused ? "Recording paused" : '<i class="pulse"></i>Recording in progress';
  paintOrbHint();
  await updateMeeting(recorder.meetingId, { status: paused ? "paused" : "recording" });
}

async function stopMeeting() {
  quietWarning(null);
  if (!recorder) return;
  const rec = recorder;
  recorder = null;
  setLiveControls(false);
  $(".recording")!.textContent = "Finalizing…";
  (byId("centerStopBtn") as HTMLButtonElement).disabled = true;
  byId("centerStopBtn")!.querySelector("span")!.textContent = "Processing…";
  enterProcessing();
  paintOrbHint();
  toast("Meeting saved. Finishing the last of the audio…");
  const { elapsedSec, chunkCount } = await rec.stop();
  await updateMeeting(rec.meetingId, { status: "processing", durationSec: elapsedSec, chunkCount, endedAt: new Date() });
  showCenter(false);
  patch({ activeMeetingId: null });
  setLiveControls(false);
  try {
    await finalizeMeeting({ meetingId: rec.meetingId });
    toast("Summary, decisions and tasks are ready");
  } catch (err) {
    toast((err as Error).message || "Timo could not finish the summary. Transcript is saved");
  } finally {
    leaveProcessing();
  }
}

/** The processing card, opened in the notes panel. Stopping a recording and uploading a file both land here. */
function enterProcessing() {
  byId("meetingProcessing")!.classList.add("show");
  const orbitHost = document.querySelector<HTMLElement>(".processing-orbit");
  if (orbitHost && !processOrb) { orbitHost.classList.add("has-orb"); processOrb = createOrb(orbitHost, 72); processOrb.setState("processing"); }
  startProcessingTicker();
  // Stopping hands the screen to the notes. The processing card lives inside the panel, and on a
  // phone that panel is hidden while recording and collapsed by default, so it has to be opened
  // here or the person is dropped back on the home page with nothing to show for the meeting.
  document.dispatchEvent(new CustomEvent("timo:recording-state", { detail: false }));
  document.dispatchEvent(new CustomEvent("timo:assistant", { detail: true }));
  setOrbs("processing");
}
function leaveProcessing() {
  setOrbs("idle");
  byId("meetingProcessing")!.classList.remove("show");
  if (processOrb) { processOrb.destroy(); processOrb = null; document.querySelector(".processing-orbit")?.classList.remove("has-orb"); }
  stopProcessingTicker();
  (document.querySelector<HTMLElement>('.tab[data-tab="summary"]'))?.click();
}

/** An uploaded file is already in Storage; run the same finish as a live recording and show the notes. */
export async function finishUploadedMeeting(id: string) {
  bindMeeting(id);
  goTo("meetings");
  enterProcessing();
  toast("Recording received. Writing up the notes…");
  try {
    await finalizeMeeting({ meetingId: id });
    toast("Summary, decisions and tasks are ready");
  } catch (err) {
    toast((err as Error).message || "Timo could not finish the summary. The transcript is saved");
  } finally {
    leaveProcessing();
  }
}

let silentSince = 0;
const onPhone = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 760;

/**
 * The warning before Timo stops a quiet recording - somewhere it will actually be seen.
 *
 * During a call Timo's tab is in the background, so a message inside the page reaches nobody. The
 * tab title counts down instead, and the panel carries a Keep recording button for anyone who is
 * looking. `null` clears it.
 */
let savedTitle = "";
function quietWarning(left: number | null) {
  if (left === null) {
    if (savedTitle) { document.title = savedTitle; savedTitle = ""; }
    byId("quietKeep")?.remove();
    return;
  }
  if (!savedTitle) savedTitle = document.title;
  const mins = Math.floor(left / 60), secs = left % 60;
  const when = mins ? `${mins}:${String(secs).padStart(2, "0")}` : `${secs}s`;
  document.title = `● Quiet - stopping in ${when} · Timo`;
  setAudioHealthNote(`Nothing heard for a while. Timo stops in ${when} unless someone speaks.`);
  const health = byId("audioHealth");
  if (health) {
    // The voice bar is hidden while things are fine; a warning has to be able to show itself.
    health.classList.add("warning");
    if (!byId("quietKeep")) {
      health.insertAdjacentHTML("beforeend", `<button type="button" class="btn quiet-keep" id="quietKeep">Keep recording</button>`);
      on(byId("quietKeep"), "click", () => { recorder?.keepListening(); quietWarning(null); toast("Still recording"); });
    }
  }
}

/** The countdown before Timo stops itself, said out loud in both places the state is shown. */
function setAudioHealthNote(text: string) {
  const title = byId("audioHealthTitle"); if (title) title.textContent = "It has gone quiet";
  const help = byId("audioHealthHelp"); if (help) help.textContent = text;
  const center = byId("centerAudioHealth");
  if (center) {
    center.style.color = "#99621e";
    center.querySelector("span:last-child")!.textContent = text;
  }
}

function setAudioHealth(level: LevelState, bars: number[]) {
  const health = byId("audioHealth")!;
  health.className = `audio-health ${level === "silent" ? "warning" : level === "loud" ? "danger" : ""}`;

  // Sustained silence on a phone almost always means another app has the microphone. A call app
  // takes it exclusively, so Timo records nothing and the person has no way to know why.
  if (level === "silent") { if (!silentSince) silentSince = Date.now(); } else silentSince = 0;
  const starved = silentSince > 0 && Date.now() - silentSince > 20_000;
  if (starved && onPhone()) {
    byId("audioHealthTitle")!.textContent = "Timo cannot hear anything";
    byId("audioHealthHelp")!.textContent = "If you are on a call on this phone, the call app is holding the microphone. Record from a second device, or take the call on a computer.";
    return;
  }
  byId("audioHealthTitle")!.textContent = level === "silent" ? "No voice detected" : level === "loud" ? "Audio is too loud" : "Voice is clear";
  byId("audioHealthHelp")!.textContent = level === "silent" ? (onPhone() ? "Speak up, or check that no other app is using the microphone" : "Check microphone or move closer") : level === "loud" ? "Move the microphone farther away" : "Microphone level looks good";
  $$("#levelMeter i").forEach((bar, i) => { bar.style.height = `${bars[i] ?? 3}px`; bar.classList.toggle("hot", level === "loud" && i > 8); });
  const center = byId("centerAudioHealth")!;
  center.style.color = level === "silent" ? "#99621e" : level === "loud" ? "#aa3f37" : "var(--green)";
  center.querySelector("span:last-child")!.textContent = byId("audioHealthTitle")!.textContent + " · " + byId("audioHealthHelp")!.textContent;
  (center.querySelector(".dot-ok") as HTMLElement).style.background = level === "silent" ? "#d19a45" : level === "loud" ? "#d56258" : "#4b9b6c";
}

/**
 * The wait after Stop, narrated. Steps mirror what the server actually does in order; the first one is
 * driven by real segment counts, the rest advance as each finishes. No invented progress percentages.
 */

const micHelp = () => /iPhone|iPad|iPod/.test(navigator.userAgent)
  ? "Microphone is blocked. Tap the aA in the address bar, open Website Settings and allow the microphone."
  : "Microphone is blocked. Allow it for timonow.com in your browser settings, then try again.";

/** The phone route for capturing a call: both voices through the microphone, on speaker. */
function showSpeakerSheet() {
  if (byId("speakerSheet")) { byId("speakerSheet")!.classList.add("show"); return; }
  document.body.insertAdjacentHTML("beforeend", `
    <div class="modal-layer show" id="speakerSheet" role="dialog" aria-modal="true" aria-labelledby="speakerTitle">
      <div class="quick-modal speaker-modal">
        <div class="modal-body">
          <b id="speakerTitle">Recording a call on this phone</b>
          <p>A phone gives the microphone to the call app alone, so Timo usually hears nothing while a WhatsApp, Zoom or normal call is running on <em>this</em> phone.</p>
          <ul class="speaker-options">
            <li><b>Use a second device</b> Put the call on speaker and record on another phone or a laptop.</li>
            <li><b>Take the call on a computer</b> There Timo can record the call audio straight from the tab.</li>
            <li><b>Record the room</b> For a meeting in person, this phone works perfectly.</li>
            <li><b>Upload a recording</b> If your phone or a call-recorder app saved the call, Timo writes it up from the file.</li>
          </ul>
        </div>
        <div class="modal-actions"><button class="btn" id="speakerCancel" type="button">Not now</button><button class="btn" id="speakerUpload" type="button">Upload a recording</button><button class="btn primary" id="speakerStart" type="button">Record anyway</button></div>
      </div>
    </div>`);
  const close = () => byId("speakerSheet")!.classList.remove("show");
  on(byId("speakerCancel"), "click", close);
  on(byId("speakerUpload"), "click", () => { close(); pickRecording(); });
  on(byId("speakerSheet"), "click", (e) => { if (e.target === byId("speakerSheet")) close(); });
  on(byId("speakerStart"), "click", async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    btn.disabled = true; btn.textContent = "Asking for the microphone…";
    const stream = await grabMicrophone();
    btn.disabled = false; btn.textContent = "Start recording";
    if (!stream) { toast(micHelp()); return; }
    close();
    void startMeeting(undefined, "mic", stream);
  });
}

const PROCESS_STEPS = [
  { key: "transcribe", label: "Transcribing the last segments" },
  { key: "read", label: "Reading the whole conversation" },
  { key: "decide", label: "Pulling out decisions and action items" },
  { key: "write", label: "Writing your notes" },
] as const;
let processTimer: number | undefined;
let processOrb: Orb | null = null;

function paintProcessing(step: number, detail: string, done = false) {
  const list = byId("processingSteps");
  if (!list) return;
  list.innerHTML = PROCESS_STEPS.map((s, i) => `<li class="${done || i < step ? "done" : i === step ? "active" : ""}"><i></i><span>${s.label}</span></li>`).join("");
  byId("processingNow")!.textContent = detail;
  const bar = byId("processingBar");
  if (bar) bar.style.width = `${done ? 100 : Math.min(96, (step / PROCESS_STEPS.length) * 100 + 8)}%`;
}

function startProcessingTicker() {
  stopProcessingTicker();
  const started = Date.now();
  const tick = () => {
    const m = viewing;
    const total = m?.chunkCount ?? 0, doneCount = m?.transcribedCount ?? 0;
    let step = 0, detail = "Collecting the recording…";
    if (total && doneCount < total) { step = 0; detail = `Transcribing segment ${Math.min(doneCount + 1, total)} of ${total}`; }
    else {
      const since = (Date.now() - started) / 1000;
      step = since < 6 ? 1 : since < 16 ? 2 : 3;
      detail = step === 1 ? "Reading the transcript end to end" : step === 2 ? "Separating decisions from discussion" : "Turning what was agreed into tasks";
    }
    paintProcessing(step, detail);
  };
  tick();
  processTimer = window.setInterval(tick, 900);
}
function stopProcessingTicker() { window.clearInterval(processTimer); processTimer = undefined; }


/**
 * The project tags on this meeting.
 *
 * Timo proposes them from what was actually said; anything typed here replaces the guess and is
 * never overwritten by a later pass. Deliberately a flat row of labels and one "Add" - a tag people
 * will not use is worse than no tag, and anything heavier than this does not get used.
 */
function renderTags(m: Meeting) {
  const host = byId("meetingTags");
  if (!host) return;
  const tags = (m.projects ?? []).filter(Boolean);
  host.hidden = false;
  host.innerHTML =
    tags.map((t, i) => `<span class="tag">${esc(t)}<button type="button" data-untag="${i}" title="Remove ${esc(t)}" aria-label="Remove ${esc(t)}">&times;</button></span>`).join("") +
    (tags.length < 6 ? `<button type="button" class="tag-add" id="addTag">+ Project</button>` : "");

  const save = (next: string[]) => updateMeeting(m.id, { projects: next, projectsEditedByUser: true });
  $$<HTMLElement>("[data-untag]", host).forEach((b) => on(b, "click", () => {
    void save(tags.filter((_, i) => i !== Number(b.dataset.untag)));
  }));
  on(byId("addTag"), "click", () => {
    // Suggest the names already in use so the same project does not end up spelled three ways.
    const known = [...new Set(state.meetings.flatMap((x) => x.projects ?? []))].filter((n) => !tags.some((t) => t.toLowerCase() === n.toLowerCase()));
    byId("addTag")!.outerHTML = `<input class="tag-input" id="tagInput" list="tagKnown" placeholder="Project name" maxlength="40" autocomplete="off"><datalist id="tagKnown">${known.map((n) => `<option value="${esc(n)}"></option>`).join("")}</datalist>`;
    const input = byId<HTMLInputElement>("tagInput")!;
    input.focus();
    const commit = () => {
      const v = input.value.trim().slice(0, 40);
      if (!v || tags.some((t) => t.toLowerCase() === v.toLowerCase())) return renderTags(m);
      void save([...tags, v]);
    };
    on(input, "keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter") { e.preventDefault(); commit(); }
      if ((e as KeyboardEvent).key === "Escape") renderTags(m);
    });
    on(input, "blur", commit);
  });
}

function renderAssistant() {
  const m = viewing;
  if (!m) return;
  const titleEl = $(".meeting-title")!;
  const editBtn = byId("editMeetingTitle"); if (editBtn) editBtn.hidden = false;
  // A live meeting re-renders every twenty seconds or so. Rewriting the title each time wiped out
  // whatever someone was typing into it, which made a meeting in progress impossible to rename.
  if (document.activeElement !== titleEl) titleEl.textContent = m.title;
  titleEl.setAttribute("contenteditable", "plaintext-only");
  titleEl.title = "Click to rename";
  titleEl.onblur = () => { const v = titleEl.textContent?.trim(); if (v && v !== m.title) void updateMeeting(m.id, { title: v }); };
  titleEl.onkeydown = (e) => { if (e.key === "Enter") { e.preventDefault(); titleEl.blur(); } };
  // Date and duration were not enough to tell two meetings apart - "Standup, Monday, 00:14:02"
  // reads the same every week. The start time is what actually identifies the one you opened.
  $(".meeting-context .meta")!.textContent =
    `${fmtLongDate(m.startedAt)} · ${m.startedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · ${fmtClock(m.durationSec)}`;
  renderTags(m);
  if (!recorder || recorder.meetingId !== m.id) { byId("timer")!.innerHTML = `${fmtClock(m.durationSec)}${m.status === "complete" ? `<small class="timer-when">${esc(relTime(m.endedAt ?? m.startedAt))}</small>` : ""}`; byId("centerTimer")!.firstChild!.nodeValue = fmtClock(m.durationSec); }
  const centerTitle = $<HTMLElement>(".center-meeting-top h1")!;
  if (document.activeElement !== centerTitle) centerTitle.textContent = m.title;
  // The title people look at during a meeting is this one, so it is the one they try to click.
  centerTitle.setAttribute("contenteditable", "plaintext-only");
  centerTitle.title = "Click to rename";
  centerTitle.onblur = () => { const v = centerTitle.textContent?.trim(); if (v && v !== m.title) { void updateMeeting(m.id, { title: v }); toast("Renamed"); } else if (!v) centerTitle.textContent = m.title; };
  centerTitle.onkeydown = (e) => { if (e.key === "Enter") { e.preventDefault(); centerTitle.blur(); } if (e.key === "Escape") { centerTitle.textContent = m.title; centerTitle.blur(); } };
  $(".center-meeting-top .meta")!.textContent = `${fmtLongDate(m.startedAt)} · started ${m.startedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
  // Several meetings a week share a title - "Standup", "1:1", "Product meeting" - so the tab has to
  // name the day too, or two open meetings are indistinguishable.
  document.title = `${m.title} · ${fmtShortDate(m.startedAt)} · Timo`;
  const live = m.status === "recording" || m.status === "paused";
  // While a meeting is running the panel is a recorder, not a document: one bar, what Timo has
  // caught, and the title. Everything for reviewing a meeting waits until there is one to review.
  byId("meetingAssistant")?.classList.toggle("is-live", live);
  if (live) {
    // The tabs are hidden while live, so make sure the one on show is the capture view.
    $$<HTMLElement>("#meetingAssistant .tab-content").forEach((t) => t.classList.toggle("active", t.id === "tab-summary"));
    $$<HTMLElement>("#meetingAssistant .meeting-tab").forEach((t) => t.classList.toggle("active", t.dataset.tab === "summary"));
  }
  const people = [...new Set([...(m.entities ?? []).filter((e) => e.type === "person").map((e) => e.name), ...(m.actionItems ?? []).map((a) => a.owner).filter(Boolean) as string[]])];
  const peopleEl = $(".meeting-context .people");
  if (peopleEl) peopleEl.innerHTML = people.slice(0, 4).map((p) => `<div class="person" title="${esc(p)}">${esc(p[0]!.toUpperCase())}</div>`).join("") + (people.length > 4 ? `<div class="person more">+${people.length - 4}</div>` : "");
  const srcEl = $(".meeting-context .source-label");
  if (srcEl) { const ev = state.events.find((e) => e.kind === "meeting" && sameDay(e.start, m.startedAt) && Math.abs(e.start.getTime() - m.startedAt.getTime()) < 30 * 60_000); srcEl.textContent = m.captureSource === "call" ? "Recorded in Timo · mic + call audio" : ev?.meetLink ? "Google Meet" : "Recorded in Timo"; }
  const summaryTab = byId("tab-summary")!;
  const summary = m.status === "complete" ? m.summary : m.summarySoFar || (live ? "Timo is listening. A rolling summary appears after the first minute." : m.status === "processing" ? "Finalizing the summary…" : m.status === "failed" ? "Summary failed. The transcript is saved. You can retry." : "");
  summaryTab.innerHTML = `
    ${live ? `<section class="content-block live-capture">
      <div class="live-head"><span class="live-dot"></span><strong>Timo is capturing</strong><span class="live-lag">notes appear about a minute behind</span></div>
      <div class="live-cols">
        <div class="live-col">
          <div class="live-col-head"><b>Notes</b><span>${(m.keyPoints ?? []).length}</span></div>
          ${(m.keyPoints ?? []).length
            ? `<ul class="live-list">${(m.keyPoints ?? []).slice(-4).map((k) => `<li>${esc(typeof k === "string" ? k : String(k))}</li>`).join("")}</ul>`
            : `<p class="live-empty">Listening…</p>`}
        </div>
        <div class="live-col">
          <div class="live-col-head"><b>Tasks</b><span>${(m.actionItems ?? []).length}</span></div>
          ${(m.actionItems ?? []).length
            ? `<ul class="live-list is-tasks">${(m.actionItems ?? []).slice(-4).map((a) => `<li>${esc(a.title)}${a.owner ? `<small>${esc(a.owner)}</small>` : ""}</li>`).join("")}</ul>`
            : `<p class="live-empty">Nothing promised yet</p>`}
        </div>
      </div>
      <div class="live-add"><button class="btn" id="liveAddNote" type="button">Add a note</button><button class="btn" id="liveAddTask" type="button">Add a task</button></div>
      <form class="live-add-form" id="liveAddForm" hidden><input id="liveAddInput" placeholder="Type it, Timo stamps it with the meeting time" autocomplete="off"><button class="btn primary" type="submit">Add</button></form>
    </section>` : ""}
    <section class="content-block"><div class="block-head"><svg class="ui-icon" viewBox="0 0 24 24"><path d="m12 3 1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3Z"></path></svg><strong>${live ? "Live summary" : "Summary"}</strong><button class="text-action" id="copySummary">Copy</button></div><p class="live-summary${m.status === "complete" ? " editable" : ""}" id="summaryText" ${m.status === "complete" ? `contenteditable="plaintext-only" data-edit="summary" title="Click to edit"` : ""}>${esc(summary)}</p></section>
    ${m.keyPoints?.length ? `<section class="content-block"><div class="block-head"><strong>Key points</strong></div><div class="key-points">${m.keyPoints.map((k, i) => `<div class="key-point"><span class="point-icon">${i + 1}</span><span class="editable" contenteditable="plaintext-only" data-edit="keyPoints" data-i="${i}" title="Click to edit">${esc(k)}</span></div>`).join("")}</div></section>` : ""}
    ${(m.decisions ?? []).map((d, i) => `<section class="content-block decision-card"><div class="block-head"><strong>Decision</strong>${d.at ? `<span class="due-pill" style="margin-left:auto">${esc(d.at)}</span>` : ""}</div><p class="editable" contenteditable="plaintext-only" data-edit="decisions" data-i="${i}" title="Click to edit">${esc(d.text)}</p></section>`).join("")}
    ${m.entities?.length ? `<section class="content-block"><div class="block-head"><strong>Mentioned</strong></div><p class="live-summary">${m.entities.map((e) => `<span class="tag">${esc(e.name)}</span>`).join(" ")}</p></section>` : ""}
    ${m.urls?.length ? `<section class="content-block"><div class="block-head"><strong>Links</strong></div><p class="live-summary">${m.urls.map((u) => `<a href="${esc(u)}" target="_blank" rel="noopener">${esc(u)}</a>`).join("<br>")}</p></section>` : ""}
    ${m.openQuestions?.length ? `<section class="content-block"><div class="block-head"><strong>Open questions</strong></div><div class="key-points">${m.openQuestions.map((q) => `<div class="key-point"><span class="point-icon">?</span><span>${esc(q)}</span></div>`).join("")}</div></section>` : ""}
    ${m.status === "failed" ? `<button class="source-link" id="retryFinalize">Retry summary</button>` : ""}
    ${m.status === "complete" && !m.notesConfirmed ? `<div class="next-step"><div><b>Check the notes</b><small>Click any line to fix it.</small></div><button class="btn primary" id="confirmNotes">${suggestedCount(m.id) ? `Looks right → ${suggestedCount(m.id)} task${suggestedCount(m.id) > 1 ? "s" : ""}` : "Looks right"}</button></div>` : ""}
    <button class="source-link" data-tab-jump="transcript">View source transcript</button>
    <p class="meta" style="margin-top:10px" id="chunkHealth">${m.transcribedCount}/${m.chunkCount} segments transcribed${m.silentChunks ? ` · ${m.silentChunks} silent skipped` : ""}${m.costUsd ? ` · ≈ $${m.costUsd.toFixed(3)} AI` : m.summaryMode === "local-stub" ? " · local stand-in, $0" : ""}${m.summaryMode === "budget-fallback" ? " · daily AI budget reached, summary is deterministic" : ""}</p>`;
  // Adding to a meeting while it is still running
  let liveAddMode: "note" | "task" = "note";
  const liveForm = byId("liveAddForm");
  const openLiveAdd = (mode: "note" | "task") => {
    liveAddMode = mode;
    if (!liveForm) return;
    liveForm.hidden = false;
    const input = byId<HTMLInputElement>("liveAddInput")!;
    input.placeholder = mode === "task" ? "What needs to happen, and who owns it" : "Type it, Timo stamps it with the meeting time";
    input.focus();
  };
  on(byId("liveAddNote"), "click", () => openLiveAdd("note"));
  on(byId("liveAddTask"), "click", () => openLiveAdd("task"));
  on(liveForm, "submit", async (e) => {
    e.preventDefault();
    const input = byId<HTMLInputElement>("liveAddInput")!;
    const text = input.value.trim();
    if (!text) return;
    input.value = "";
    const at = fmtMMSS(Math.max(0, Math.floor((Date.now() - m.startedAt.getTime()) / 1000)));
    try {
      if (liveAddMode === "task") {
        await createTask({ title: text, status: "open", source: "user", meetingId: m.id, durationMin: 30 });
        toast("Task added to this meeting");
      } else {
        await addManualNote(m.id, { at, text });
        toast("Note added");
      }
    } catch (err) {
      toast((err as Error).message || "Could not add that");
    }
    if (liveForm) liveForm.hidden = true;
  });

  on(byId("confirmNotes"), "click", async () => {
    await updateMeeting(m.id, { notesConfirmed: true });
    if (suggestedCount(m.id)) document.querySelector<HTMLElement>('.tab[data-tab="tasks"]')?.click();
    else { setExpanded(false); toast("Notes saved"); }
  });
  on(byId("copySummary"), "click", () => navigator.clipboard.writeText((byId("summaryText")?.textContent ?? summary ?? "").trim()).then(() => toast("Summary copied")));
  // Inline edits save on blur (or Enter for single-line items); an emptied item is removed.
  $$<HTMLElement>("[data-edit]", summaryTab).forEach((el) => {
    on(el, "focus", () => setExpanded(true));
    on(el, "keydown", (ev) => { const k = ev as KeyboardEvent; if (k.key === "Escape") { el.blur(); } if (k.key === "Enter" && el.dataset.edit !== "summary") { k.preventDefault(); el.blur(); } });
    on(el, "blur", async () => {
      const text = (el.textContent ?? "").trim();
      const field = el.dataset.edit!;
      if (field === "summary") { if (text !== (m.summary ?? "").trim()) await updateMeeting(m.id, { summary: text }); return; }
      const i = Number(el.dataset.i);
      if (field === "keyPoints") { const next = [...(m.keyPoints ?? [])]; if (text) next[i] = text; else next.splice(i, 1); if (next.join("\n") !== (m.keyPoints ?? []).join("\n")) await updateMeeting(m.id, { keyPoints: next }); }
      if (field === "decisions") { const next = [...(m.decisions ?? [])]; if (text) next[i] = { ...next[i]!, text }; else next.splice(i, 1); if (JSON.stringify(next) !== JSON.stringify(m.decisions ?? [])) await updateMeeting(m.id, { decisions: next }); }
    });
  });
  on(byId("retryFinalize"), "click", async () => { toast("Retrying…"); try { await finalizeMeeting({ meetingId: m.id }); } catch (e) { toast((e as Error).message); } });
  $$("[data-tab-jump]", summaryTab).forEach((b) => on(b, "click", () => document.querySelector<HTMLElement>(`.tab[data-tab="${b.dataset.tabJump}"]`)?.click()));

  // Suggested tasks tab. While live, action items come from the rolling state; once finalized they are
  // real `suggested` tasks in the tasks collection (single source of truth shared with the AI inbox).
  const suggestedTasks = state.tasks.filter((t) => t.meetingId === m.id && t.status === "suggested");
  const approvedTasks = state.tasks.filter((t) => t.meetingId === m.id && t.status !== "suggested");
  const liveItems = m.status === "complete" ? [] : (m.actionItems ?? []);
  const rows = m.status === "complete"
    ? suggestedTasks.map((t) => ({ id: t.id, title: t.title, owner: t.notes?.replace(/^Owner: /, "") ?? "", at: t.evidence?.quote ? "" : "", due: t.dueAt ? t.dueAt.toLocaleDateString() : "", confidence: t.confidence ?? 0 }))
    : liveItems.map((a, i) => ({ id: `live-${i}`, title: a.title, owner: a.owner ?? "", at: a.at ?? "", due: a.due ?? "", confidence: a.confidence }));
  const tasksTab = byId("tab-tasks")!;
  (document.querySelector(".tab[data-tab='tasks'] .tab-count") as HTMLElement).textContent = String(rows.length);
  tasksTab.innerHTML = `${!rows.length && approvedTasks.length ? "" : ""}${m.status === "complete" && rows.length ? `<div class="next-step plain"><div><b>Approve what becomes a task</b><small>Timo finds time inside your working hours.</small></div></div>` : ""}<div class="task-toolbar"${!rows.length && approvedTasks.length ? ' hidden' : ""}><span><b id="selectedTaskCount">0</b> of ${rows.length} selected</span><button id="selectAllMeetingTasks">Select all</button></div>` +
    (rows.length ? rows.map((a) => `<label class="meeting-task"><input class="check meeting-task-check" type="checkbox" data-id="${a.id}" ${a.confidence >= 0.7 ? "checked" : ""} ${m.status !== "complete" ? "disabled" : ""}><span class="meeting-task-title">${esc(a.title)}<span class="meeting-task-meta"><span class="owner-dot">${esc((a.owner || "?")[0]!.toUpperCase())}</span>${esc(a.owner || "Unassigned")}${a.at ? ` · from ${esc(a.at)}` : ""} · ${Math.round(a.confidence * 100)}% sure</span></span><span class="due-pill">${a.due ? esc(a.due) : "No date"}</span>${m.status === "complete" ? `<button type="button" class="row-details" data-open-task="${a.id}" title="Open task details" aria-label="Open task details">›</button>` : ""}</label>`).join("") : approvedTasks.length
      ? `<p class="meta" style="padding:2px 2px 8px">All set, ${approvedTasks.length} task${approvedTasks.length > 1 ? "s" : ""} from this meeting:</p>` + approvedTasks.map((t) => `<button type="button" class="meeting-task done-task" data-open-task="${t.id}"><span class="meeting-task-title">${esc(t.title)}<span class="meeting-task-meta">${t.status === "done" ? "Done" : t.scheduledStart ? `Scheduled ${fmtShortDate(t.scheduledStart)} · ${fmtRange(t.scheduledStart, t.scheduledEnd ?? t.scheduledStart)}` : t.dueAt ? `Due ${t.dueAt.toLocaleDateString()}` : "No date yet"}</span></span><span class="due-pill">Details</span></button>`).join("")
      : `<p class="lead" style="padding:8px 2px">${live ? "Action items appear here as they are spoken." : m.status === "processing" ? "Extracting action items…" : "No action items came out of this meeting."}</p>`) +
    `<div class="schedule-footer"${!rows.length && approvedTasks.length ? ' hidden' : ""}><button class="schedule-btn" id="scheduleAll" disabled><span id="scheduleButtonText">${m.status === "complete" ? "Select tasks to schedule" : "Available after the meeting ends"}</span></button></div>`;
  $$("[data-open-task]", tasksTab).forEach((b) => on(b, "click", (ev) => {
    ev.preventDefault(); ev.stopPropagation();
    const t = state.tasks.find((x) => x.id === (b as HTMLElement).dataset.openTask);
    if (t) openTaskDetail(t);
  }));
  const checks = $$<HTMLInputElement>(".meeting-task-check", tasksTab);
  const sync = () => { const n = checks.filter((c) => c.checked && !c.disabled).length; byId("selectedTaskCount")!.textContent = String(n); (byId("scheduleAll") as HTMLButtonElement).disabled = !n; if (m.status === "complete") { byId("scheduleButtonText")!.textContent = n ? `Approve and schedule ${n} task${n > 1 ? "s" : ""}` : "Select tasks to schedule"; } byId("selectAllMeetingTasks")!.textContent = n === checks.length && n ? "Clear all" : "Select all"; };
  checks.forEach((c) => on(c, "change", sync));
  on(byId("selectAllMeetingTasks"), "click", () => { const all = !checks.every((c) => c.checked); checks.forEach((c) => (c.checked = all)); sync(); });
  on(byId("scheduleAll"), "click", async () => {
    const ids = checks.filter((c) => c.checked && !c.disabled).map((c) => c.dataset.id!);
    for (const id of ids) await updateTask(id, { status: "open" }, "user");
    await scheduleTaskIds(ids);
  });
  sync();

  // Notes tab: automatic notes + your own timestamped notes, with an inline composer
  const auto = [...(m.decisions ?? []).map((d) => ({ t: d.at ?? "", b: "Decision", p: d.text })), ...(m.keyPoints ?? []).map((k) => ({ t: "", b: "Key point", p: k })), ...(m.researchTopics ?? []).map((r) => ({ t: "", b: "Research", p: r }))];
  const manual = (m.manualNotes ?? []).map((n) => ({ t: n.at, b: "You", p: n.text }));
  const all = [...manual, ...auto].sort((a, b) => a.t.localeCompare(b.t));
  byId("tab-notes")!.innerHTML = `<div class="task-toolbar"><span>${auto.length} automatic · ${manual.length} yours</span><button id="addManualNote">＋ Add note</button></div><form id="manualNoteForm" class="ask-meeting" style="margin:0 0 10px" hidden><input id="manualNoteInput" placeholder="Type or speak a note, it's stamped with the meeting time" aria-label="Note"><button type="button" class="mic-btn" id="noteVoiceInline" aria-label="Dictate note" title="Dictate"><svg class="ui-icon" viewBox="0 0 24 24"><rect x="9" y="3" width="6" height="10" rx="3"></rect><path d="M6 11a6 6 0 0 0 12 0M12 17v3"></path></svg></button><button aria-label="Save">↵</button></form><div class="notes-list">${all.map((n) => `<div class="note-item"><time>${esc(n.t)}</time><b>${n.b}</b><p>${esc(n.p)}</p></div>`).join("") || `<p class="lead" style="padding:8px 2px">Notes build up as the meeting runs. Add your own any time.</p>`}</div>`;
  on(byId("noteVoiceInline"), "click", () => dictateInto(byId<HTMLInputElement>("manualNoteInput")!, byId("noteVoiceInline")!));
  on(byId("addManualNote"), "click", () => { const f = byId("manualNoteForm")!; f.hidden = !f.hidden; if (!f.hidden) byId("manualNoteInput")!.focus(); });
  on(byId("manualNoteForm"), "submit", async (e) => { e.preventDefault(); const input = byId<HTMLInputElement>("manualNoteInput")!; const text = input.value.trim(); if (!text) return; await addManualNote(m.id, { at: recorder && recorder.meetingId === m.id ? fmtMMSS(recorder.elapsedSec) : fmtMMSS(m.durationSec), text }); input.value = ""; toast("Note added"); });

  // Live notes on the recording center
  const liveNotes = $(".center-live-grid .center-panel:first-child")!;
  liveNotes.innerHTML = `<div class="center-panel-head"><b>Live notes</b><span>${m.summarySoFar ? "Updated just now" : "Listening…"}</span></div>${m.summarySoFar ? `<div class="live-note-row"><time>${fmtMMSS(m.durationSec)}</time><p>${esc(m.summarySoFar)}</p></div>` : ""}${(m.decisions ?? []).slice(-3).map((d) => `<div class="live-note-row"><time>${esc(d.at ?? "")}</time><p><b>Decision:</b> ${esc(d.text)}</p></div>`).join("")}`;
  const liveTasks = $(".center-live-grid .center-panel:last-child")!;
  liveTasks.innerHTML = `<div class="center-panel-head"><b>Action items so far</b><span>${rows.length} found</span></div>${rows.slice(0, 4).map((a) => `<label class="center-task-row"><input class="check" type="checkbox" disabled><span><b>${esc(a.title)}</b><small>${esc(a.owner || "Unassigned")}${a.at ? ` · from ${esc(a.at)}` : ""}</small></span><span class="tag">${a.due ? esc(a.due) : "No date"}</span></label>`).join("")}`;
}

function renderTranscript() {
  const q = (byId<HTMLInputElement>("transcriptSearch")?.value ?? "").toLowerCase();
  const lines = byId("tab-transcript")!.querySelector(".transcript-lines")!;
  lines.innerHTML = segments.length
    ? segments.filter((s) => !q || s.text.toLowerCase().includes(q)).map((s) => `<div class="transcript-line"><time>${fmtMMSS(s.offsetSec)}</time>${esc(s.text)}</div>`).join("")
    : `<p class="lead" style="padding:8px 2px">Words show up here about 20 seconds after they are spoken.</p>`;
}

export async function stopMeetingFromCommand() {
  if (!recorder) return toast("No meeting is recording");
  await stopMeeting();
}

export async function scheduleTaskIds(ids: string[]) {
  if (!ids.length) return;
  toast("Finding free time…");
  try {
    const { data } = await scheduleTasks({ taskIds: ids });
    const placed = data.results.filter((r) => r.eventId && r.start);
    const missed = data.results.find((r) => r.reason);
    if (placed.length) {
      const first = new Date(placed[0]!.start!);
      const when = `${relativeDue(first)} ${first.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
      showWeekOf(first);
      toast(placed.length === 1 ? `Scheduled for ${when}` : `${placed.length} tasks scheduled · first ${when}${missed ? ` · ${data.results.length - placed.length} need a later date` : ""}`);
    } else toast(missed?.reason ?? "No free time found");
  } catch (err) {
    toast((err as Error).message);
  }
}

export function setupMeeting() {
  document.addEventListener("timo:record", ((e: CustomEvent<string>) => void startMeeting(e.detail)) as EventListener);
  // Keyboard shortcuts live in the shell; it only knows "toggle recording" and "toggle mute".
  document.addEventListener("timo:record-toggle", () => void (recorder ? stopMeetingFromCommand() : startMeeting()));
  // On a phone the recording view covers the screen. Going somewhere else has to release it, or the
  // app looks stuck; the red "still recording" chip is how you come back.
  onPageChange(() => { if (recorder && window.innerWidth <= 760) showCenter(false); });
  document.addEventListener("timo:mute-toggle", () => { if (recorder) toggleMute(); });
  on(byId("pauseBtn"), "click", togglePause);
  on(byId("centerPauseBtn"), "click", togglePause);
  on(byId("editMeetingTitle"), "click", async () => { const m = viewing; if (!m) return; const t = prompt("Meeting name", m.title)?.trim(); if (t && t !== m.title) { await updateMeeting(m.id, { title: t }); toast("Renamed"); } });
  on(byId("muteBtn"), "click", toggleMute);
  on(byId("centerMuteBtn"), "click", toggleMute);
  on(byId("stopBtn"), "click", stopMeeting);
  on(byId("centerStopBtn"), "click", stopMeeting);
  on(byId("startMeetingFromHistory"), "click", () => void startMeeting());
  byId("startMeetingFromHistory")?.insertAdjacentHTML("beforebegin", `<button class="btn" id="uploadRecordingBtn" type="button" title="A call your phone recorded, a voice memo, a file from another device">Upload a recording</button>`);
  on(byId("uploadRecordingBtn"), "click", pickRecording);
  on(byId("backToDay"), "click", () => showCenter(false));
  on(byId("returnToMeeting"), "click", () => showCenter(true));
  on(byId("transcriptSearch"), "input", renderTranscript);
  on(byId("audioTest"), "click", () => toast("Levels update live while recording"));
  document.addEventListener("click", (e) => { const b = (e.target as HTMLElement).closest<HTMLElement>("[data-open-meeting]"); if (b) openMeeting(b.dataset.openMeeting!); });
  document.addEventListener("click", (e) => {
    const sh = (e.target as HTMLElement).closest<HTMLElement>("[data-share-meeting]");
    if (sh) { e.stopPropagation(); const id = sh.dataset.shareMeeting!; openShare(id, state.meetings.find((m) => m.id === id)?.title ?? "Meeting"); return; }
    const pd = (e.target as HTMLElement).closest<HTMLElement>("[data-pdf-meeting]");
    if (pd) { e.stopPropagation(); void downloadExport(pd.dataset.pdfMeeting!, "pdf"); }
  });
  // Ask about this meeting: grounded answers with timestamp citations
  byId("noteForm")!.insertAdjacentHTML("beforebegin", `<div class="ask-thread" id="askThread"></div>`);
  // Hand the whole meeting to an outside assistant: summary + decisions + transcript, with a clear prompt.
  byId("noteForm")!.insertAdjacentHTML("afterend", `<div class="continue-in"><span>Continue in</span>
    <button type="button" data-chat="Claude" title="Open Claude with this meeting"><img src="/logos/claude.svg" alt="Claude"></button>
    <button type="button" data-chat="ChatGPT" title="Open ChatGPT with this meeting"><img src="/logos/openai.svg" alt="ChatGPT"></button>
    <button type="button" data-chat="Gemini" title="Open Gemini with this meeting"><img src="/logos/googlegemini.svg" alt="Gemini"></button>
  </div>`);
  $$("[data-chat]").forEach((b) => on(b, "click", () => void continueInChat(b.dataset.chat as ChatApp)));
  // Ask by voice: dictation fills the box and sends it, so a question mid-meeting needs no typing.
  on(byId("askVoiceBtn"), "click", () => dictateInto(byId<HTMLInputElement>("noteInput")!, byId("askVoiceBtn")!, true));
  on(byId("noteForm"), "submit", async (e) => {
    e.preventDefault();
    const input = byId<HTMLInputElement>("noteInput")!;
    const q = input.value.trim();
    if (!q || !viewing) return;
    input.value = "";
    const thread = byId("askThread")!;
    thread.insertAdjacentHTML("beforeend", `<div class="ask-q">${esc(q)}</div><div class="ask-a" data-pending>Looking through the transcript…</div>`);
    thread.scrollTop = thread.scrollHeight;
    try {
      const { data } = await askMeeting({ meetingId: viewing.id, question: q });
      const a = thread.querySelector("[data-pending]")!;
      a.removeAttribute("data-pending");
      a.innerHTML = `${esc(data.answer)}${data.citations.length ? `<div class="ask-cites">${data.citations.map((c) => `<button type="button" data-jump="${esc(c.at)}">${esc(c.at)}</button>`).join("")}</div>` : ""}`;
      a.querySelectorAll<HTMLElement>("[data-jump]").forEach((b) => on(b, "click", () => { byId<HTMLInputElement>("transcriptSearch")!.value = ""; renderTranscript(); document.querySelector<HTMLElement>('.tab[data-tab="transcript"]')?.click(); const line = [...byId("tab-transcript")!.querySelectorAll(".transcript-line")].find((l) => l.querySelector("time")?.textContent === b.dataset.jump); line?.scrollIntoView({ block: "center" }); line?.classList.add("hit"); }));
    } catch (err) {
      const a = thread.querySelector("[data-pending]"); if (a) { a.removeAttribute("data-pending"); a.textContent = (err as Error).message; }
    }
  });
  // "Test warnings" was a prototype toggle; levels are live now
  byId("audioTest")?.remove();
  // ⋯ menu
  const moreBtn = document.querySelector<HTMLElement>('.assistant-head [aria-label="More options"]');
  if (moreBtn) {
    moreBtn.insertAdjacentHTML("afterend", `<div class="more-menu" id="meetingMenu" hidden></div>`);
    on(moreBtn, "click", (e) => { e.stopPropagation(); renderMenu(); const mm = byId("meetingMenu")!; mm.hidden = !mm.hidden; });
    document.addEventListener("click", (e) => { if (!(e.target as HTMLElement).closest("#meetingMenu")) { const mm = byId("meetingMenu"); if (mm) mm.hidden = true; } });
  }
  showCenter(false);
  const wave = document.querySelector<HTMLElement>(".big-wave");
  if (wave) { wave.insertAdjacentHTML("beforebegin", `<div class="orb-host" id="orbHost"></div>`); wave.remove(); }
  heroOrb = createOrb(byId("orbHost")!, 168);
  heroOrb.setState("idle");
  // The big orb is a control, not a picture: one tap pauses or continues, two taps mute.
  // Idle, one tap starts (the microphone is taken inside the tap, as iOS requires).
  const orbHost = byId("orbHost")!;
  orbHost.setAttribute("role", "button"); orbHost.tabIndex = 0; orbHost.setAttribute("aria-label", "Recording control");
  orbHost.insertAdjacentHTML("afterend", `<small class="orb-hint" id="orbHint">Tap to start recording</small>`);
  let tapTimer = 0;
  const orbTap = async () => {
    if (!recorder) { const stream = await grabMicrophone(); if (!stream) return toast(micHelp()); return void startMeeting(undefined, "mic", stream); }
    if (tapTimer) { window.clearTimeout(tapTimer); tapTimer = 0; toggleMute(); return; }
    tapTimer = window.setTimeout(() => { tapTimer = 0; void togglePause(); }, 280);
  };
  on(orbHost, "click", () => void orbTap());
  on(orbHost, "keydown", (e) => { const k = e as KeyboardEvent; if (k.key === "Enter" || k.key === " ") { k.preventDefault(); void orbTap(); } });
  const icon = document.querySelector<HTMLElement>(".record-icon");
  if (icon) {
    // The orb doubles as the quick way in: one click on it starts a recording.
    icon.innerHTML = ""; icon.classList.add("live", "card-orb");
    cardOrb = createOrb(icon, 48); cardOrb.setState("idle");
    const peek = byId("peekOrb");
    if (peek && !peekOrb) { peekOrb = createOrb(peek, 36); peekOrb.setState("idle"); const open = () => document.dispatchEvent(new CustomEvent("timo:assistant", { detail: true })); on(peek, "click", open); on(peek, "keydown", (e) => { const k = e as KeyboardEvent; if (k.key === "Enter" || k.key === " ") { k.preventDefault(); open(); } }); }
    icon.setAttribute("role", "button"); icon.tabIndex = 0;
    icon.title = "Start recording (R)"; icon.setAttribute("aria-label", "Start recording");
    const go = () => { if (!recorder) void startMeeting(); };
    on(icon, "click", go);
    on(icon, "keydown", (e) => { const k = e as KeyboardEvent; if (k.key === "Enter" || k.key === " ") { k.preventDefault(); go(); } });
  }
  renderEmptyAssistant();
  $(".recording")!.textContent = "Not recording";
  byId("timer")!.textContent = "00:00:00";
  byId("centerTimer")!.firstChild!.nodeValue = "00:00:00";
  // Give the assistant a "start" affordance when idle
  const rc = document.querySelector(".record-actions");
  if (rc && !byId("startBtn")) {
    rc.insertAdjacentHTML("afterbegin", `<button class="control-btn ai-btn" id="startBtn" title="Start new meeting notes. Timo listens, writes them up and pulls out the tasks"><span class="btn-orb" id="startBtnOrb"></span><span>New meeting notes</span></button>${`<button class="control-btn" id="startCallBtn" title="${canCaptureCall() ? "Capture you and the other people on a Meet, Zoom or Teams call running in another tab" : "Put the call on speaker and Timo records both sides through the microphone"}"><svg class="ui-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="5" width="13" height="14" rx="2"></rect><path d="M16 10l5-3v10l-5-3z"></path></svg><span>Record call</span></button>`}`);
    // The orb is the record mark: it sits on the button at rest and comes alive while recording
    const startHost = byId("startBtnOrb");
    if (startHost && !buttonOrb) { buttonOrb = createOrb(startHost, 22); buttonOrb.setState("idle"); }
    // Phone bottom bar: the orb is the record button, and it is the first thing people meet after signing in
    const barHost = byId("barRecordOrb");
    if (barHost && !barOrb) {
      barOrb = createOrb(barHost, 34); barOrb.setState("idle");
      on(byId("barRecord"), "click", async () => {
        // On a phone the recording centre is the recording view, so leave the notes panel alone
        if (window.innerWidth > 760) document.dispatchEvent(new CustomEvent("timo:assistant", { detail: true }));
        // Already recording: the bar brings you back to the recording. Stop is a deliberate button there.
        if (recorder) { goTo("today"); showCenter(true); return; }
        // Same reason as everywhere else: the microphone has to be asked for inside the tap.
        const stream = await grabMicrophone();
        if (!stream) return toast(micHelp());
        void startMeeting(undefined, "mic", stream);
      });
    }
    on(byId("startBtn"), "click", async () => {
      const stream = await grabMicrophone();
      if (!stream) return toast(micHelp());
      void startMeeting(undefined, "mic", stream);
    });
    on(byId("startCallBtn"), "click", () => {
      if (canCaptureCall()) return void startMeeting(undefined, "call");
      // No browser on a phone can reach another app's audio: iOS and Android both forbid it, for
      // WhatsApp, Zoom and ordinary phone calls alike. Speaker plus the microphone is the way that
      // works, and it works for every calling app, so say so plainly instead of failing quietly.
      showSpeakerSheet();
    });
    $(".recording")?.insertAdjacentHTML("afterend", `<span class="capture-source" id="captureSource" hidden></span>`);
  }
  setLiveControls(false);
  // Reflect the auto-notes/tasks toggles into preferences
  $$<HTMLInputElement>("#autoNotesToggle, .mirror-auto-notes").forEach((t) => on(t, "change", () => document.dispatchEvent(new CustomEvent("timo:pref", { detail: { autoNotes: t.checked } }))));
  $$<HTMLInputElement>("#autoTasksToggle, .mirror-auto-tasks").forEach((t) => on(t, "change", () => document.dispatchEvent(new CustomEvent("timo:pref", { detail: { autoTasks: t.checked } }))));
}


/** Replace the prototype's static assistant content with an honest empty state. */
function renderEmptyAssistant() {
  $(".meeting-title")!.textContent = "No meeting open";
  const editBtn = byId("editMeetingTitle"); if (editBtn) editBtn.hidden = true;
  $(".meeting-context .meta")!.textContent = "Press Record to capture one, or open a past meeting";
  const people = $(".meeting-context .people"); if (people) people.innerHTML = "";
  const src = $(".meeting-context .source-label"); if (src) src.textContent = "";
  byId("tab-summary")!.innerHTML = `<section class="content-block"><div class="block-head"><strong>Summary</strong></div><p class="live-summary">Timo listens while you record and turns the conversation into a summary, decisions and tasks.</p></section>`;
  byId("tab-notes")!.innerHTML = `<div class="task-toolbar"><span>No notes yet</span></div>`;
  byId("tab-tasks")!.innerHTML = `<div class="task-toolbar"><span>No action items yet</span></div>`;
  (document.querySelector(".tab[data-tab='tasks'] .tab-count") as HTMLElement).textContent = "0";
  byId("tab-transcript")!.querySelector(".transcript-lines")!.innerHTML = "";
  $(".center-meeting-top h1")!.textContent = "Meeting";
  $(".center-live-grid .center-panel:first-child")!.innerHTML = `<div class="center-panel-head"><b>Live notes</b><span>Listening…</span></div>`;
  $(".center-live-grid .center-panel:last-child")!.innerHTML = `<div class="center-panel-head"><b>Action items so far</b><span>0 found</span></div>`;
}

/** Called whenever meetings change: if nothing is bound yet, show the most recent meeting. */
export function bindLatestIfIdle(meetings: { id: string }[]) {
  if (viewing || recorder || !meetings.length) return;
  bindMeeting(meetings[0]!.id);
}

type ChatApp = "Claude" | "ChatGPT" | "Gemini";
const CHAT_URL: Record<ChatApp, (q: string) => string> = {
  Claude: (q) => `https://claude.ai/new?q=${q}`,
  ChatGPT: (q) => `https://chatgpt.com/?q=${q}`,
  Gemini: () => "https://gemini.google.com/app",
};

/** The full meeting as text: what it was, what Timo captured, and the transcript. */
function meetingAsPrompt(m: Meeting) {
  const lines = [
    `Here is a meeting I recorded in Timo. Help me act on it: check the summary is right, list anything we missed, and draft the follow-up messages I owe people.`,
    "",
    `# ${m.title}, ${fmtLongDate(m.startedAt)} (${fmtClock(m.durationSec)})`,
    m.summary || m.summarySoFar ? `\n## Summary\n${m.summary || m.summarySoFar}` : "",
    m.keyPoints?.length ? `\n## Key points\n${m.keyPoints.map((k) => `- ${k}`).join("\n")}` : "",
    m.decisions?.length ? `\n## Decisions\n${m.decisions.map((d) => `- ${d.text}`).join("\n")}` : "",
    m.openQuestions?.length ? `\n## Open questions\n${m.openQuestions.map((q) => `- ${q}`).join("\n")}` : "",
    segments.length ? `\n## Transcript\n${segments.map((x) => `[${fmtMMSS(x.offsetSec)}] ${x.text}`).join("\n")}` : "",
  ];
  return lines.filter(Boolean).join("\n");
}

/**
 * Short meetings travel in the URL so the chat opens ready to answer; long ones go to the clipboard,
 * because every chat app truncates a very long query string.
 */
async function continueInChat(app: ChatApp) {
  const m = viewing;
  if (!m) return toast("Open a meeting first");
  const text = meetingAsPrompt(m);
  const encoded = encodeURIComponent(text);
  const fits = app !== "Gemini" && encoded.length < 6000;
  if (!fits) {
    try { await navigator.clipboard.writeText(text); toast(`Meeting copied. Paste it into ${app} (⌘V)`); }
    catch { toast("Couldn't copy the meeting to the clipboard"); return; }
  }
  window.open(fits ? CHAT_URL[app](encoded) : CHAT_URL[app](encodeURIComponent(`I'm pasting a meeting from Timo, help me act on it.`)), "_blank", "noopener");
}

function renderMenu() {
  const mm = byId("meetingMenu")!;
  const m = viewing;
  if (!m) { mm.innerHTML = `<button disabled>No meeting open</button>`; return; }
  const retention = { "delete-after-processing": "Delete after processing", "24h": "Keep 24 hours", "7d": "Keep 7 days", keep: "Keep audio" } as const;
  mm.innerHTML = `
    <button data-act="share">Share notes…</button>
    <button data-act="pdf">Download PDF</button>
    <button data-act="doc">Download Word (.doc)</button>
    <button data-act="rename">Rename meeting</button>
    <button data-act="copy">Copy transcript</button>
    <button data-act="export">Export notes (.md)</button>
    <button data-act="notebook">Draw as a notebook page</button>
    <button data-act="retention">Audio: ${retention[m.audioRetention]} ↻</button>
    <button data-act="delete" class="danger">Delete meeting…</button>`;
  $$("[data-act]", mm).forEach((b) => on(b, "click", async () => {
    mm.hidden = true;
    switch (b.dataset.act) {
      case "share": openShare(m.id, m.title); break;
      case "pdf": case "doc": await downloadExport(m.id, b.dataset.act as "pdf" | "doc"); break;
      case "rename": { const t = prompt("Meeting name", m.title)?.trim(); if (t && t !== m.title) await updateMeeting(m.id, { title: t }); break; }
      case "notebook": { toast("Drawing the page…"); const { downloadNotebook } = await import("./notebook"); await downloadNotebook(m); break; }
      case "copy": await navigator.clipboard.writeText(segments.map((s) => `[${fmtMMSS(s.offsetSec)}] ${s.text}`).join("\n")); toast("Transcript copied"); break;
      case "export": {
        const md = [`# ${m.title}`, `${fmtLongDate(m.startedAt)} · ${fmtClock(m.durationSec)}`, "", "## Summary", m.summary || m.summarySoFar || "", "", m.detailedNotes || "", "", "## Decisions", ...(m.decisions ?? []).map((d) => `- ${d.text}${d.at ? ` (${d.at})` : ""}`), "", "## Action items", ...state.tasks.filter((t) => t.meetingId === m.id).map((t) => `- [${t.status === "done" ? "x" : " "}] ${t.title}${t.dueAt ? ` · due ${t.dueAt.toLocaleDateString()}` : ""}`), "", "## Your notes", ...(m.manualNotes ?? []).map((n) => `- [${n.at}] ${n.text}`), "", "## Transcript", ...segments.map((s) => `[${fmtMMSS(s.offsetSec)}] ${s.text}`)].join("\n");
        const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([md], { type: "text/markdown" })); a.download = `${m.title.replace(/[^\w-]+/g, "-")}.md`; a.click(); URL.revokeObjectURL(a.href); toast("Notes exported"); break;
      }
      case "retention": { const order = ["delete-after-processing", "24h", "7d", "keep"] as const; const next = order[(order.indexOf(m.audioRetention) + 1) % order.length]!; await updateMeeting(m.id, { audioRetention: next }); toast(`Audio retention: ${retention[next]}`); break; }
      case "delete": { if (!confirm(`Delete ${m.title} and its transcript? Suggested tasks from it are removed; approved tasks stay.`)) return; if (recorder?.meetingId === m.id) await stopMeeting(); await deleteMeetingFn({ meetingId: m.id }); unwatchMeeting?.(); unwatchTranscript?.(); viewing = null; segments = []; renderEmptyAssistant(); toast("Meeting deleted"); break; }
    }
  }));
}
// ---------------------------------------------------------------------------
// Sharing and export
// ---------------------------------------------------------------------------
let shareMeetingId = "";
const shareStatus = (text: string, tone: "busy" | "ok" | "warn" | "" = "") => { const el = byId("shareStatus")!; el.hidden = !text; el.textContent = text; el.className = `say-status ${tone}`; };

/** Downloads the same PDF/Word a recipient would get. Built on the server so every copy matches. */
export async function downloadExport(meetingId: string, format: "pdf" | "doc") {
  toast(format === "pdf" ? "Building the PDF…" : "Building the document…");
  try {
    const { data } = await exportMeetingFn({ meetingId, format, includeTranscript: format === "pdf" ? byId<HTMLInputElement>("shareTranscript")?.checked ?? false : true });
    const bytes = Uint8Array.from(atob(data.base64), (c) => c.charCodeAt(0));
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([bytes], { type: data.mime }));
    a.download = data.filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  } catch (e) { toast((e as Error).message); }
}

export function openShare(meetingId: string, title: string) {
  shareMeetingId = meetingId;
  const modal = byId("shareModal")!;
  byId("shareSubtitle")!.textContent = `${title}. Anyone with the link can read the notes. You can turn it off any time.`;
  byId<HTMLInputElement>("shareLink")!.value = "";
  byId<HTMLInputElement>("shareEmails")!.value = "";
  byId<HTMLTextAreaElement>("shareNote")!.value = "";
  byId("shareRevoke")!.hidden = true;
  shareStatus("");
  modal.classList.add("show"); modal.setAttribute("aria-hidden", "false");
  void createLink();
}
const closeShare = () => { const m = byId("shareModal")!; m.classList.remove("show"); m.setAttribute("aria-hidden", "true"); };

/** The link exists as soon as the sheet opens. One click to copy, nothing to configure first. */
async function createLink() {
  shareStatus("Creating the link…", "busy");
  try {
    const { data } = await shareMeetingFn({ meetingId: shareMeetingId, origin: location.origin, includeTranscript: byId<HTMLInputElement>("shareTranscript")!.checked });
    byId<HTMLInputElement>("shareLink")!.value = data.url;
    byId("shareRevoke")!.hidden = false;
    shareStatus("Link ready, copy it, or send it by email below.", "ok");
  } catch (e) { shareStatus((e as Error).message, "warn"); }
}

export function setupShare() {
  on(byId("closeShare"), "click", closeShare);
  on(byId("shareDone"), "click", closeShare);
  on(byId("shareModal"), "click", (e) => e.target === byId("shareModal") && closeShare());
  on(byId("shareTranscript"), "change", () => void createLink());
  on(byId("shareCopy"), "click", async () => {
    const url = byId<HTMLInputElement>("shareLink")!.value;
    if (!url) return;
    try { await navigator.clipboard.writeText(url); shareStatus("Link copied to your clipboard.", "ok"); } catch { byId<HTMLInputElement>("shareLink")!.select(); shareStatus("Press ⌘C to copy the selected link.", "warn"); }
  });
  on(byId("shareRevoke"), "click", async () => {
    if (!confirm("Turn off this link? Anyone who has it will stop being able to open the notes.")) return;
    try { await revokeShareFn({ meetingId: shareMeetingId }); byId<HTMLInputElement>("shareLink")!.value = ""; byId("shareRevoke")!.hidden = true; shareStatus("Link turned off. Sharing again creates a new one.", "ok"); }
    catch (e) { shareStatus((e as Error).message, "warn"); }
  });
  on(byId("shareForm"), "submit", async (e) => {
    e.preventDefault();
    const emails = byId<HTMLInputElement>("shareEmails")!.value.split(/[,\s;]+/).map((x) => x.trim()).filter(Boolean);
    if (!emails.length) return shareStatus("Add at least one email address, or just copy the link.", "warn");
    const btn = byId<HTMLButtonElement>("shareSend")!;
    btn.disabled = true;
    shareStatus("Sending…", "busy");
    try {
      const { data } = await shareMeetingFn({ meetingId: shareMeetingId, origin: location.origin, includeTranscript: byId<HTMLInputElement>("shareTranscript")!.checked, emails, note: byId<HTMLTextAreaElement>("shareNote")!.value, attachPdf: byId<HTMLInputElement>("shareAttach")!.checked });
      byId<HTMLInputElement>("shareLink")!.value = data.url;
      shareStatus(`Sent to ${data.sent} ${data.sent === 1 ? "person" : "people"} with the link${byId<HTMLInputElement>("shareAttach")!.checked ? " and the PDF attached" : ""}.`, "ok");
      byId<HTMLInputElement>("shareEmails")!.value = "";
    } catch (err) { shareStatus((err as Error).message, "warn"); }
    btn.disabled = false;
  });
  document.addEventListener("timo:escape", closeShare);
}
